import "./../styles/HeadBar.style.css";

import resourceTypes, { EnvironmentsData } from "../models/ResourcesTypes";
import statusTypes from "../models/StatusType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faRotate,
  faClockRotateLeft,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";

interface HeadBarProps {
  environments: EnvironmentsData;
  datetime: string;
  filter: string;
  setFilter: (value: string) => void;
  timeframe: string;
  setTimeframe: (value: string) => void;
  gridView: string;
  setGridView: (value: string) => void;
  statusType: string;
  resourceType: string;
  setReload: (reload: boolean) => void;
  anyFilter: boolean;
  zeroRun: boolean;
  healthStatus: "green" | "yellow" | "red" | null;
  autoRefresh: boolean;
  setAutoRefresh: (v: boolean) => void;
  autoRefreshInterval: number;
  setAutoRefreshInterval: (v: number) => void;
  onLogout?: () => void;
}

const HeadBar = (props: HeadBarProps) => {
  const {
    environments,
    datetime,
    filter,
    setFilter,
    timeframe,
    setTimeframe,
    gridView,
    setGridView,
    statusType,
    resourceType,
    setReload,
    anyFilter,
    zeroRun,
    healthStatus,
    autoRefresh,
    setAutoRefresh,
    autoRefreshInterval,
    setAutoRefreshInterval,
    onLogout,
  } = props;

  const [isSettingCreds, setIsSettingCreds] = useState(false);
  const gridViews = ["2", "4", "6", "8"];
  const timeframes = ["6h", "12h", "24h", "48h", "72h"];
  const [credValues, setCredValues] = useState<Record<string, string>>();

  useEffect(() => {
    if (isSettingCreds) {
      const initialValues: Record<string, string> = {};
      environments &&
        Object.keys(environments)
          .filter((env) => !env.includes("gitlab"))
          .forEach((env) => {
            initialValues[env] = localStorage.getItem(env) || "";
          });
      setCredValues(initialValues);
    }
  }, [environments, isSettingCreds]);

  function openingCreds() {
    if (!environments) {
      setReload(true);
    }
    setIsSettingCreds(true);
  }

  function handleChange(env: string, value: string) {
    setCredValues((prev) => ({ ...prev, [env]: value }));
  }

  function onSave() {
    if (credValues) {
      Object.entries(credValues).forEach(([env, value]) => {
        localStorage.setItem(env, value);
      });
      setIsSettingCreds(false);
      setReload(true);
    }
  }

  return (
    <div className="headbar-container">
      {isSettingCreds && environments && (
        <div className="credentials-container">
          <div className="credentials-card">
            {Object.keys(environments)
              .filter((env) => !env.includes("gitlab"))
              .map((env) => (
                <div key={env} className="env-field">
                  <h2>{env}</h2>
                  <textarea
                    name={env}
                    value={credValues ? credValues[env] : ""}
                    onChange={(e) => {
                      handleChange(env, e.target.value);
                    }}
                  />
                </div>
              ))}
            <div className="credentials-controls">
              <button onClick={() => onSave()}>confirm</button>
              <button
                className="secondary"
                onClick={() => setIsSettingCreds(false)}
              >
                cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="tools">
        <div
          className="icon"
          onClick={() => openingCreds()}
          title="Credentials"
        >
          <FontAwesomeIcon icon={faKey}></FontAwesomeIcon>
        </div>
        {onLogout && (
          <div className="icon" onClick={onLogout} title="Sign out">
            <FontAwesomeIcon icon={faRightFromBracket}></FontAwesomeIcon>
          </div>
        )}
        {healthStatus && (
          <div
            className="health-badge"
            style={{
              backgroundColor:
                healthStatus === "green"
                  ? "var(--success-color)"
                  : healthStatus === "yellow"
                    ? "#F5A623"
                    : "var(--error-color)",
            }}
            title={`Health: ${healthStatus}`}
          >
            <span className="health-label">
              {healthStatus === "green"
                ? "OK"
                : healthStatus === "yellow"
                  ? "WARN"
                  : "ALERT"}
            </span>
          </div>
        )}
      </div>
      <div className="controls">
        <span className="date">{datetime}</span>
        {anyFilter && (
          <span style={{ color: "var(--primary-color)" }}>any %</span>
        )}
        {zeroRun && (
          <span style={{ color: "var(--primary-color)" }}>0 run</span>
        )}
        {statusType && (
          <div
            className="status"
            style={{
              background: statusTypes.find((st) => st.name === statusType)
                ?.color,
            }}
          ></div>
        )}
        {resourceType && (
          <span
            className="label"
            style={{
              background: resourceTypes.find((rt) => rt.name === resourceType)
                ?.color,
            }}
          >
            {resourceType}
          </span>
        )}
        <input
          type="text"
          placeholder="Search..."
          onChange={(e) => setFilter(e.target.value)}
          value={filter}
        />
        <div className="datepicker">
          {gridViews.map((gv) => (
            <span
              key={gv}
              onClick={() => setGridView(gv)}
              style={{
                color:
                  gv === gridView
                    ? "var(--primary-color)"
                    : "var(--text-color)",
              }}
            >
              {gv}
            </span>
          ))}
        </div>
        <div className="datepicker">
          {timeframes.map((tf) => (
            <span
              key={tf}
              onClick={() => setTimeframe(tf)}
              style={{
                color:
                  tf === timeframe
                    ? "var(--primary-color)"
                    : "var(--text-color)",
              }}
            >
              {tf}
            </span>
          ))}
        </div>
        <div className="icon">
          <FontAwesomeIcon
            icon={faRotate}
            onClick={() => setReload(true)}
          ></FontAwesomeIcon>
        </div>
        <div
          className={`icon ${autoRefresh ? "auto-refresh-active" : ""}`}
          onClick={() => setAutoRefresh(!autoRefresh)}
          title={
            autoRefresh
              ? `Auto-refresh: every ${autoRefreshInterval / 60000}min (click to disable)`
              : "Enable auto-refresh"
          }
        >
          <FontAwesomeIcon
            icon={faClockRotateLeft}
            spin={autoRefresh}
          ></FontAwesomeIcon>
        </div>
        {autoRefresh && (
          <select
            value={autoRefreshInterval}
            onChange={(e) =>
              setAutoRefreshInterval(parseInt(e.target.value, 10))
            }
            className="auto-refresh-select"
          >
            <option value={60000}>1 min</option>
            <option value={300000}>5 min</option>
            <option value={600000}>10 min</option>
            <option value={1800000}>30 min</option>
          </select>
        )}
      </div>
    </div>
  );
};

export default HeadBar;
